import React from 'react';
import { X, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import DocumentTypeBadge from './document-type-badge';

interface FileSelectionListProps {
  files: File[];
  onRemoveFile: (file: File) => void;
  documentsMetadata?: Record<string, any>;
  readOnly?: boolean;
}

export function FileSelectionList({
  files,
  onRemoveFile,
  documentsMetadata = {},
  readOnly = false
}: FileSelectionListProps) {
  if (files.length === 0) return null;

  // Debug logging
  console.log('FileSelectionList received:', {
    files: files.map(f => f.name),
    documentsMetadata,
    fileKeys: Object.keys(documentsMetadata)
  });

  return (
    <div className="space-y-2 pl-6">
      <div className="relative">
        {/* Main vertical line */}
        <div className="absolute left-0 top-0 bottom-2 w-0.5 bg-border rounded-full" />

        <AnimatePresence>
          {files.map((file, index) => {
            const fileMetadata = documentsMetadata[file.name];
            const documentCategory = fileMetadata?.documentCategory;

            // Debug for each file
            console.log(`File ${index}:`, {
              fileName: file.name,
              hasMetadata: !!fileMetadata,
              documentCategory: documentCategory || 'none'
            });

            return (
              <motion.div
                key={file.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="relative"
              >
                {/* Branch line */}
                <div className="absolute left-0 top-1/2 w-4 h-0.5 bg-border -translate-x-4" />

                {/* Node circle */}
                <div className="absolute left-0 top-1/2 w-2 h-2 rounded-full bg-border -translate-x-5 -translate-y-1" />

                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg border mb-2",
                  "bg-background hover:bg-accent/5 border-input"
                )}>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3">
                      {documentCategory ? (
                        <DocumentTypeBadge
                          documentType={documentCategory}
                          documentDetails={fileMetadata}
                          className="w-9 h-9 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-transparent border border-primary">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{file.name}</span>
                        {documentCategory && (
                          <span className="text-xs text-muted-foreground capitalize">
                            {documentCategory}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!readOnly && <button
                    onClick={() => onRemoveFile(file)}
                    className="text-muted-foreground hover:text-foreground rounded-full p-1 hover:bg-accent/10 transition-colors"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove {file.name}</span>
                  </button>}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
